/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer, PushContent } from "../types";
import { STUB_SHOP } from "../stub";
import { CharCounter, Field, PhoneFrame, SampleCustomerPicker, TemplateInput, useOfferText, usePreview } from "../shared";

type PushEditorProps = {
  content: PushContent;
  offer: CampaignOffer;
  onChange: (content: PushContent) => void;
};

const LIMITS = { title: 40, body: 120 };

export const PushEditor = defineComponent<PushEditorProps>({
  name: "PushEditor",
  props: ["content", "offer", "onChange"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();
    const offerText = useOfferText();
    const patch = (p: Partial<PushContent>) => props.onChange({ ...props.content, ...p });

    return () => {
      const c = props.content;
      return (
        <div class="flex h-full min-h-0 overflow-hidden">
          <div class="min-w-0 flex-1 space-y-4 overflow-y-auto p-6">
            <div class="flex gap-3 rounded-xl bg-base-200 p-3 text-sm">
              <span class="material-symbols-outlined text-primary">wallet</span>
              <p class="text-base-content/70">{t("campaigns.studio_push.explainer")}</p>
            </div>
            <Field label={t("campaigns.studio_push.title")} counter={CharCounter(c.title.length, LIMITS.title)}>
              <TemplateInput channel="push" maxLength={LIMITS.title} value={c.title} onChange={(v) => patch({ title: v })} />
            </Field>
            <Field label={t("campaigns.studio_push.body")} counter={CharCounter(c.body.length, LIMITS.body)}>
              <TemplateInput channel="push" maxLength={LIMITS.body} value={c.body} onChange={(v) => patch({ body: v })} />
            </Field>
            <label class="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                class="toggle toggle-primary toggle-sm"
                checked={c.update_pass_offer}
                onChange={(e) => patch({ update_pass_offer: (e.target as HTMLInputElement).checked })}
              />
              <span class="label-text">{t("campaigns.studio_push.update_pass")}</span>
            </label>
          </div>

          <div class="flex w-[380px] shrink-0 flex-col items-center gap-4 overflow-y-auto border-l border-base-300 bg-base-200 p-6">
            <SampleCustomerPicker />
            <PhoneFrame background="linear-gradient(160deg, oklch(var(--p)) 0%, oklch(var(--in)) 100%)">
              <div class="px-4 pt-6 text-center text-primary-content">
                <div class="text-sm opacity-80">{t("campaigns.studio_push.lock_date")}</div>
                <div class="text-6xl font-light">9:41</div>
              </div>
              <div class="mx-3 mt-6 rounded-2xl bg-base-100/90 p-3 shadow-lg backdrop-blur">
                <div class="flex items-center gap-2 text-[11px] text-base-content/60">
                  <span class="flex h-5 w-5 items-center justify-center rounded bg-neutral text-neutral-content">
                    <span class="material-symbols-outlined text-[13px]">wallet</span>
                  </span>
                  <span class="flex-1 uppercase tracking-wide">Wallet</span>
                  <span>{t("campaigns.studio_push.now")}</span>
                </div>
                <div class="mt-1 text-sm font-semibold">{preview.render(c.title)}</div>
                <div class="text-sm text-base-content/80">{preview.render(c.body)}</div>
              </div>
              {c.update_pass_offer && (
                <div class="mx-6 mt-auto mb-6 rounded-2xl bg-neutral p-4 text-neutral-content shadow-xl">
                  <div class="flex items-center justify-between text-xs opacity-80">
                    <span class="font-semibold">{STUB_SHOP.name}</span>
                    <span>{t("campaigns.studio_push.points", [preview.customer.value.points_balance])}</span>
                  </div>
                  <div class="mt-3 text-[10px] uppercase tracking-widest opacity-60">{t("campaigns.studio_push.current_offer")}</div>
                  <div class="text-lg font-bold">{offerText(props.offer) || "—"}</div>
                  <div class="mt-3 flex justify-center rounded-lg bg-base-100 py-2">
                    <span class="material-symbols-outlined text-5xl text-base-content">qr_code_2</span>
                  </div>
                </div>
              )}
            </PhoneFrame>
          </div>
        </div>
      );
    };
  },
});
